// /app/property — the demo's Property Management page, which also holds what was the Maintenance group and, since
// 2026-10-22, the tenancies and changeovers that were on Lettings: six sub-tabs (?sub=), each behind its own permission page.
//   overview       the portfolio at a glance (workOrders, compliance or inspections; GET /property/overview)
//   works          every job (workOrders) — the old Work orders page; "My jobs" for a single-record scope
//   certificates   every compliance certificate (compliance) — the old Compliance page
//   visits         every inspection (inspections)
//   tenancies      every tenancy (tenancies; ?filter= a tile, ?inArrears=true, ?search=) — a tenancy keeps /app/tenancies/:id
//   changeovers    flats between tenancies (changeovers; ?propertyId=, ?phase=, ?filter=)
// A sub-tab shows only to someone who can read its page; with one sub-tab there is no tab row. Visibility follows the
// permissions only; the data inside each sub-tab follows the user's scope (Works says "My jobs" for a single-record scope). The old
// addresses (/app/work-orders, /app/compliance, /app/inspections, /app/tenancies, /app/changeovers) redirect here (App.tsx);
// a job keeps its own page at /app/work-orders/:id.
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../stores/auth';
import { RequirePermission } from '../components/RequirePermission';
import { PropertyOverviewTab } from '../components/portfolio/PropertyOverviewTab';
import { WorksTab } from '../components/portfolio/WorksTab';
import { CertificatesTab } from '../components/portfolio/CertificatesTab';
import { VisitsTab } from '../components/portfolio/VisitsTab';
import { TenanciesTab } from '../components/lettings/TenanciesTab';
import { ChangeoversTab } from '../components/lettings/ChangeoversTab';
import { Tabs } from '../components/ui';

const PROPERTY_SUBS = [
  { key: 'overview', label: 'Overview', pages: ['workOrders', 'compliance', 'inspections'], Tab: PropertyOverviewTab },
  { key: 'works', label: 'Works', pages: ['workOrders'], Tab: WorksTab },
  { key: 'certificates', label: 'Certificates', pages: ['compliance'], Tab: CertificatesTab },
  { key: 'visits', label: 'Visits', pages: ['inspections'], Tab: VisitsTab },
  { key: 'tenancies', label: 'Tenancies', pages: ['tenancies'], Tab: TenanciesTab },
  { key: 'changeovers', label: 'Changeovers', pages: ['changeovers'], Tab: ChangeoversTab },
] as const;
type PropertySub = (typeof PROPERTY_SUBS)[number]['key'];

export default function PropertyPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const subs = PROPERTY_SUBS.filter((s) => s.pages.some((p) => can(`${p}.read`)));
  const sub = subs.find((s) => s.key === params.get('sub')) ?? subs[0];
  // Switching sub-tab drops the other one's state (?filter=, ?propertyId=, ?search=…).
  const go = (k: PropertySub) => setParams(k === subs[0]?.key ? {} : { sub: k });
  if (!sub) return <RequirePermission page="workOrders">{null}</RequirePermission>;
  const { Tab } = sub;
  return (
    <div>
      {subs.length > 1 && (
        <Tabs className="mb-2.5" items={subs.map((s) => ({ key: s.key, label: s.label }))} value={sub.key} onChange={go} />
      )}
      <Tab key={sub.key} />
    </div>
  );
}
